import { Button } from '@/components/ui/button'
import { Check, PauseCircle, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { DeliveryFeePopover } from '@/components/counter/rail/DeliveryFeePopover'
import { ServiceChargePopover } from '@/components/counter/rail/ServiceChargePopover'
import type { ServiceChargeOverride } from '@/types'

export interface ActionFooterTotals {
  subtotal: number
  service: number
  tax: number
  taxRate: number
  /** Service-charge fraction (0.10 == 10%); optional so legacy callers keep working. */
  serviceRate?: number
  /** Flat delivery add-on (after tax); omit or 0 to hide. */
  delivery?: number
  /**
   * FBR POS service fee (F-MM-N9) — flat per invoice, after tax and never in
   * the tax base. Omit or 0 to hide, which is every tenant that has not opted
   * in and every PRA tenant.
   */
  posFee?: number
  total: number
  discount?: number
  /** F-DEAL-N3: names the discount ("Deal · Buy 1 Get 1 Pizza"); default "Discount". */
  discountLabel?: string
  /** Discount percent (0–100) when the discount was entered as a percentage. */
  discountPercent?: number | null
  /**
   * Sum of per-line manual discounts. Shown on its own row above the
   * bill-level Discount row so the cashier can see both totals.
   */
  itemDiscounts?: number
  /** F-MM-N23: 'marketplace_settled' hides the Sales Tax row (the platform remits it). */
  taxTreatment?: 'standard' | 'marketplace_settled'
}

/**
 * Render a fractional rate like 0.075 as "7.5%" / 0.15 as "15%" — matches the
 * convention used on the printed receipt so the UI and the bill agree.
 */
function formatRatePct(fraction: number): string {
  if (!Number.isFinite(fraction) || fraction <= 0) return ''
  const rounded = Math.round(fraction * 10000) / 100
  return `${rounded}%`
}

export type ActionFooterMode = 'idle' | 'compose' | 'checkout'

export interface ActionFooterProps {
  mode: ActionFooterMode
  totals: ActionFooterTotals | null
  formatCurrency: (n: number) => string
  /** Label for the primary CTA (compose mode). */
  primaryLabel: string
  onPrimary?: () => void
  primaryDisabled?: boolean
  primaryPending?: boolean
  /**
   * F-FE-N158: park the cart ("Hold") so the next guest can be served. Omit to
   * hide the button — it is only offered for a new, un-placed cart.
   */
  onHold?: () => void
  holdDisabled?: boolean
  holdPending?: boolean
  /** Why Hold is unavailable right now (e.g. offline), shown under the button. */
  holdHint?: string
  /** Message shown under totals when action is disabled (e.g. “Select a table first”). */
  disabledHint?: string
  /** Shown in checkout mode as a secondary row (the primary Charge CTA lives in the payment panel for color accuracy).
   *  Labelled "Back to ticket" (F-FE-N144): it leaves the payment screen and
   *  keeps the order, so it must not share the word "close" with the header
   *  control that releases the ticket entirely. */
  onCloseCheckout?: () => void
  /**
   * When checkout is open but the footer is in compose mode (staged cart lines),
   * show Back to ticket under the Fire KOT primary so operators can dismiss pay
   * without losing the ability to send staged items.
   */
  showCloseCheckoutWithCompose?: boolean
  /** When the bottom CTA must not render (e.g. no cart and no session). */
  hidePrimary?: boolean
  /**
   * Delivery order — render the fee row even at 0 with an inline pencil editor.
   * onEditDelivery applies a number (override) or null (reset to default).
   */
  deliveryEditable?: boolean
  deliveryOverridden?: boolean
  deliveryDefault?: number
  deliveryPending?: boolean
  onEditDelivery?: (next: number | null) => void
  /**
   * Service charge — inline pencil editor on the row (any order type).
   * onEditServiceCharge applies an override (percent or fixed amount) or null
   * (reset to the settings rate). The row itself always renders; the pencil
   * only when onEditServiceCharge is supplied.
   */
  serviceChargeOverride?: ServiceChargeOverride | null
  serviceChargeDefaultRate?: number
  serviceChargePending?: boolean
  onEditServiceCharge?: (next: ServiceChargeOverride | null) => void
}

export function ActionFooter({
  mode,
  totals,
  formatCurrency,
  primaryLabel,
  onPrimary,
  primaryDisabled,
  primaryPending,
  disabledHint,
  onCloseCheckout,
  showCloseCheckoutWithCompose,
  hidePrimary,
  deliveryEditable,
  deliveryOverridden,
  deliveryDefault,
  deliveryPending,
  onEditDelivery,
  serviceChargeOverride,
  serviceChargeDefaultRate,
  serviceChargePending,
  onEditServiceCharge,
  onHold,
  holdDisabled,
  holdPending,
  holdHint,
}: ActionFooterProps) {
  if (mode === 'idle') {
    return (
      <div className="border-t border-border/80 bg-card/95 px-4 py-3 text-xs text-muted-foreground sm:px-5">
        {disabledHint ?? 'Pick a table or order type to begin.'}
      </div>
    )
  }

  const showTotals = totals !== null

  return (
    <div
      role="group"
      aria-label="Ticket actions"
      className="space-y-3 border-t border-border/90 bg-card/95 px-4 py-3 shadow-[0_-8px_30px_-12px_rgba(0,0,0,0.12)] backdrop-blur-md supports-[backdrop-filter]:bg-card/85 dark:shadow-[0_-8px_30px_-12px_rgba(0,0,0,0.45)] sm:px-5 sm:py-4"
    >
      {showTotals && (
        <div className="space-y-1 text-sm leading-relaxed">
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Subtotal</span>
            <span className="font-medium tabular-nums">{formatCurrency(totals!.subtotal)}</span>
          </div>
          {typeof totals!.itemDiscounts === 'number' && totals!.itemDiscounts > 0 && (
            <div className="flex justify-between gap-4 text-primary">
              <span>Item Discounts</span>
              <span className="font-medium tabular-nums">−{formatCurrency(totals!.itemDiscounts)}</span>
            </div>
          )}
          {typeof totals!.discount === 'number' && totals!.discount > 0 && (
            <div className="flex justify-between gap-4 text-primary">
              <span>
                {totals!.discountLabel ?? 'Discount'}
                {typeof totals!.discountPercent === 'number' && totals!.discountPercent > 0
                  ? ` (${formatRatePct(totals!.discountPercent / 100)})`
                  : ''}
              </span>
              <span className="font-medium tabular-nums">−{formatCurrency(totals!.discount)}</span>
            </div>
          )}
          <div className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              Service Charges{totals!.serviceRate ? ` (${formatRatePct(totals!.serviceRate)})` : ''}
              {serviceChargeOverride && (
                <span className="rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                  {serviceChargeOverride.kind === 'amount' ? 'fixed' : 'custom'}
                </span>
              )}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="font-medium tabular-nums">{formatCurrency(totals!.service)}</span>
              {onEditServiceCharge && (
                <ServiceChargePopover
                  currentAmount={totals!.service}
                  currentRate={totals!.serviceRate ?? 0}
                  override={serviceChargeOverride ?? null}
                  defaultRate={serviceChargeDefaultRate ?? 0}
                  onApply={(n) => onEditServiceCharge(n)}
                  onReset={() => onEditServiceCharge(null)}
                  pending={serviceChargePending}
                />
              )}
            </span>
          </div>
          {totals!.taxTreatment === 'marketplace_settled' ? (
            <div className="text-xs text-muted-foreground">Tax settled by marketplace</div>
          ) : (
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">
                Sales Tax{totals!.taxRate ? ` (${formatRatePct(totals!.taxRate)})` : ''}
              </span>
              <span className="font-medium tabular-nums">{formatCurrency(totals!.tax)}</span>
            </div>
          )}
          {(totals!.posFee ?? 0) > 0 && (
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">FBR POS Fee</span>
              <span className="font-medium tabular-nums">{formatCurrency(totals!.posFee ?? 0)}</span>
            </div>
          )}
          {deliveryEditable ? (
            <div className="flex items-center justify-between gap-4">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                Delivery fee
                {deliveryOverridden && (
                  <span className="rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                    custom
                  </span>
                )}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="font-medium tabular-nums">{formatCurrency(totals!.delivery ?? 0)}</span>
                {onEditDelivery && (
                  <DeliveryFeePopover
                    current={totals!.delivery ?? 0}
                    overridden={Boolean(deliveryOverridden)}
                    defaultFee={deliveryDefault ?? 0}
                    onApply={(n) => onEditDelivery(n)}
                    onReset={() => onEditDelivery(null)}
                    pending={deliveryPending}
                  />
                )}
              </span>
            </div>
          ) : (
            typeof totals!.delivery === 'number' &&
            totals!.delivery > 0 && (
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">Delivery fee</span>
                <span className="font-medium tabular-nums">{formatCurrency(totals!.delivery)}</span>
              </div>
            )
          )}
          <div className="flex justify-between border-t border-border pt-2 text-base font-bold tracking-tight sm:text-lg">
            <span>Total</span>
            <span className="tabular-nums">{formatCurrency(totals!.total)}</span>
          </div>
        </div>
      )}

      {mode === 'compose' && !hidePrimary && (
        <Button
          type="button"
          className={cn('h-12 w-full text-base font-semibold sm:h-14 sm:text-lg')}
          disabled={primaryDisabled || primaryPending}
          onClick={onPrimary}
        >
          {primaryPending ? (
            'Sending…'
          ) : (
            <>
              <Check className="mr-2 h-5 w-5" />
              {primaryLabel}
            </>
          )}
        </Button>
      )}

      {mode === 'compose' && onHold && (
        <Button
          type="button"
          variant="outline"
          data-testid="counter-hold-cart"
          className="h-10 w-full border-amber-300 bg-amber-50 font-semibold text-amber-900 hover:bg-amber-100 hover:text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-900/50"
          disabled={holdDisabled || holdPending}
          onClick={onHold}
          title="Park this order so you can serve the next guest"
        >
          <PauseCircle className="mr-2 h-4 w-4" />
          {holdPending ? 'Holding…' : 'Hold'}
        </Button>
      )}
      {mode === 'compose' && onHold && holdHint && (
        <p className="text-center text-[11px] text-muted-foreground">{holdHint}</p>
      )}

      {mode === 'compose' && showCloseCheckoutWithCompose && onCloseCheckout && (
        <Button
          type="button"
          variant="outline"
          className="h-10 w-full border-red-200 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800 dark:border-red-800/40 dark:bg-red-950/30 dark:text-red-400 dark:hover:bg-red-950/50"
          onClick={onCloseCheckout}
        >
          <X className="mr-2 h-4 w-4" />
          Back to ticket
        </Button>
      )}

      {mode === 'checkout' && onCloseCheckout && (
        <Button
          type="button"
          variant="outline"
          className="h-10 w-full border-red-200 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800 dark:border-red-800/40 dark:bg-red-950/30 dark:text-red-400 dark:hover:bg-red-950/50"
          onClick={onCloseCheckout}
        >
          <X className="mr-2 h-4 w-4" />
          Back to ticket
        </Button>
      )}

      {disabledHint && (
        <p className="text-[11px] text-muted-foreground text-center">{disabledHint}</p>
      )}
    </div>
  )
}
